import { Subject, Subscription } from 'rxjs';
import { safeStringify } from 'commons/utils/safeStringify';
import type {
  AgentChatActionBusState,
  ComponentController,
  InternalActionTool,
  AgentNavigationContent,
} from './AgentChatActionBusTypes';
import { AI_FIELD_NAVIGATION_ATTRIBUTES } from '../../hooks/useAiField/constants';
import { logError } from '../../logger';

/**
 * Manages the state of the Chat Agent AI system.
 * It provides a way to register components, dispatch actions, and get the current state, using RxJS.
 */
export class AgentChatActionBus {
  private readonly actionStream = new Subject<InternalActionTool>();
  private readonly componentRegistry = new Map<
    string,
    ComponentController<unknown, unknown>
  >();
  private actionSub: Subscription | null = null;

  private isInitialized = false;
  private isScrollingInChain = false;
  private hasScrolledInChain = false;

  private ensureInitialized() {
    if (!this.isInitialized) {
      this.setupActionProcessing();
      this.isInitialized = true;
    }
  }

  public register<T = unknown, V = unknown>(
    componentId: string,
    controller: ComponentController<T, V>,
  ) {
    this.componentRegistry.set(
      componentId,
      // Casting needed because TypeScript doesn't know the generic types of the controller.
      controller as ComponentController<unknown, unknown>,
    );
  }

  public unregister(componentId: string) {
    this.componentRegistry.delete(componentId);
  }

  public dispatch(action: InternalActionTool) {
    this.ensureInitialized();
    this.actionStream.next(action);
  }

  public getCurrentState(): AgentChatActionBusState {
    const state: AgentChatActionBusState = {};

    this.componentRegistry.forEach((controller, componentId) => {
      try {
        if (controller.getValue) {
          state[componentId] = {
            value: controller.getValue(),
          };
        }
      } catch (error) {
        logError('Error getting state', error, {
          componentId,
        });
      }
    });

    return state;
  }

  public dispatchBatch(actions: InternalActionTool[]) {
    this.ensureInitialized();
    this.isScrollingInChain = false;
    this.hasScrolledInChain = false;

    actions.forEach((action) => {
      this.dispatch(action);
    });
  }

  private setupActionProcessing() {
    this.actionSub = this.actionStream.subscribe((action) => {
      try {
        this.executeAction(action);
      } catch (error) {
        logError('Error executing action', error, {
          target: action.target,
          operation: action.operation,
        });
      }
    });
  }

  /**
   * Handles navigation by validating the URL and using the registered navigation controller.
   * Supports both page navigation (internal routes) and element scrolling (hashtag links).
   */
  private handleNavigation(content: AgentNavigationContent) {
    if (!content.url) {
      logError('Navigation content missing URL', new Error(), { content });
      return;
    }

    const { url } = content;

    const navigationController = this.componentRegistry.get('navigation');

    if (!navigationController?.onReplace) {
      logError('No navigation controller registered', new Error(), {
        url,
      });
      return;
    }

    navigationController.onReplace(url);
  }

  /**
   * Scrolls to the element with the given data attribute.
   * 1. We only support scrolling to existing and rendered elements, until Agent Routing navigation is implemented.
   * 2. We use a data attribute to identify the element to scroll to, so we can easily find it in the DOM.
   */
  private scrollToElement(target: string) {
    if (this.hasScrolledInChain || this.isScrollingInChain) {
      return;
    }

    // Finds the element by direct attribute or by merged attributes
    const element =
      document.querySelector(
        `[${AI_FIELD_NAVIGATION_ATTRIBUTES}="${target}"]`,
      ) ||
      Array.from(
        document.querySelectorAll(`[${AI_FIELD_NAVIGATION_ATTRIBUTES}]`),
      ).find((el) =>
        el
          .getAttribute(AI_FIELD_NAVIGATION_ATTRIBUTES)
          ?.split(',')
          .includes(target),
      );

    if (!element) {
      logError(
        'No element found with data attribute',
        new Error('No element found with data attribute'),
        { target },
      );
      return;
    }

    this.isScrollingInChain = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          (element as HTMLElement).scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });

          this.hasScrolledInChain = true;
          this.isScrollingInChain = false;
        } catch (error) {
          this.isScrollingInChain = false;
          this.hasScrolledInChain = true;
          logError('Failed to scroll to element', error, {
            target,
          });
        }
      });
    });
  }

  public executeScrollToElement(target: string) {
    this.hasScrolledInChain = false;
    this.isScrollingInChain = false;
    this.scrollToElement(target);
  }

  private safeExecuteAction(
    action: InternalActionTool,
    handler?: (content: InternalActionTool['content']) => void,
  ): boolean {
    if (!handler) {
      return false;
    }

    try {
      handler(action.content);
      return true;
    } catch (error) {
      logError('Error executing action', error, {
        target: action.target,
        operation: action.operation,
      });
      return false;
    }
  }

  private executeAction(action: InternalActionTool) {
    const controller = this.componentRegistry.get(action.target);

    if (!controller) {
      logError(
        'No controller found for target',
        new Error('No controller found for target'),
        {
          target: action.target,
          operation: action.operation,
        },
      );
      return;
    }

    let actionExecuted = false;

    switch (action.operation) {
      case 'replace': {
        actionExecuted = this.safeExecuteAction(action, controller.onReplace);
        break;
      }

      case 'add': {
        actionExecuted = this.safeExecuteAction(action, controller.onAdd);
        break;
      }

      case 'remove': {
        actionExecuted = this.safeExecuteAction(action, controller.onRemove);
        break;
      }

      case 'clear': {
        actionExecuted = this.safeExecuteAction(action, controller.onClear);
        break;
      }

      case 'navigate': {
        actionExecuted = this.safeExecuteAction(action, () =>
          this.handleNavigation(action.content),
        );
        break;
      }

      default:
        logError(
          'Received an unknown operation',
          new Error('Received an unknown operation'),
          {
            action: safeStringify(action),
          },
        );
    }

    if (actionExecuted) {
      this.scrollToElement(action.target);
    }
  }

  /**
   * Cleans up all active subscriptions to prevent memory leaks.
   * Should be called when the instance is no longer needed (e.g., in tests or during teardown).
   */
  public dispose() {
    if (this.actionSub) {
      this.actionSub.unsubscribe();
      this.actionSub = null;
    }

    this.componentRegistry.clear();
    this.isInitialized = false;
  }
}
